import React, { useState } from "react";

// Collapsible task-description panel.
//
// The complete task description is presented once, on the first prediction
// page. Every screen after that offers the same control instead: the
// description is hidden by default and one click brings it back. Labels come
// from taskConfig.detailsToggle, which the static-synthetic group conditions
// override to "Show task" and "Hide task"; the per-task loan and scenario
// fallbacks are kept for every other caller. `scrollable` matches the chat
// page's existing max-h-72 box.

export function TaskDetailsPanel({
  taskConfig,
  open,
  onToggle,
  scrollable = false,
}) {
  const DetailsComponent = taskConfig?.DetailsComponent;
  const detailsToggle = taskConfig?.detailsToggle || {};
  const detailsTitle = taskConfig?.detailsTitle || "Case details";

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{detailsTitle}</h3>
        <button
          type="button"
          className="shrink-0 text-sm px-3 py-2 rounded-md font-semibold shadow-sm border border-empirica-500 text-white bg-empirica-600 hover:bg-empirica-700 focus:outline-none focus:ring-2 focus:ring-empirica-300"
          aria-expanded={open}
          onClick={onToggle}
        >
          {open
            ? detailsToggle.hide || "🙈 Hide case"
            : detailsToggle.show || "👁️ Show case"}
        </button>
      </div>
      {open ? (
        scrollable ? (
          <div className="max-h-72 overflow-y-auto pr-2">
            {DetailsComponent ? <DetailsComponent /> : null}
          </div>
        ) : DetailsComponent ? (
          <DetailsComponent />
        ) : null
      ) : (
        <div className="text-sm text-gray-600 border border-dashed border-gray-300 rounded-md p-3 bg-gray-50">
          {detailsToggle.hiddenText ||
            "Case hidden. Click \"Show case\" to view the details again."}
        </div>
      )}
    </>
  );
}

// The same panel with its own collapsed-by-default state, for callers that have
// no reason to own the state themselves.
export function CollapsibleTaskDetails({
  taskConfig,
  scrollable = false,
  defaultOpen = false,
  className = "bg-white rounded-lg shadow-md p-6 mb-6",
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (!taskConfig?.DetailsComponent) return null;
  return (
    <div className={className}>
      <TaskDetailsPanel
        taskConfig={taskConfig}
        open={open}
        onToggle={() => setOpen((prev) => !prev)}
        scrollable={scrollable}
      />
    </div>
  );
}
